import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  alpha,
  Skeleton,
} from "@mui/material";
import { Edit, Trash2, Eye } from "lucide-react";
import { useGetList } from "../../../../hooks/useGetList";
import { useParams } from "react-router-dom";
import ProductItemRow from "./ProductItemRow";
import { useDispatch, useSelector } from "react-redux";
import { clearEditProductItem } from "../../../../redux/productItem/productItemSlice";

const ProductItemList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const { id } = useParams();
  const dispatch = useDispatch();

  const {
    data: productItems,
    loading,
    fetchData,
    refetch,
  } = useGetList(`/medicine-items?relatedProduct=${id}`);
  const { refresh } = useSelector((state) => state.refresh);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    dispatch(clearEditProductItem());
  }, []);

  useEffect(() => {
    refetch();
  }, [refresh]);

  return (
    <Paper elevation={2} sx={{ width: "100%", overflow: "hidden" }}>
      <Box sx={{ p: 3, borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}>
        <Typography variant="h6" component="div">
          Product Items
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Manage your product inventory
        </Typography>
      </Box>

      <TableContainer>
        <Table stickyHeader aria-label="product items table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, minWidth: 50 }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>
                Name
              </TableCell>

              <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>
                Code
              </TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>
                Barcode
              </TableCell>

              <TableCell sx={{ fontWeight: 600, minWidth: 300 }}>
                Description
              </TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 50 }}>Unit</TableCell>

              <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
                Created
              </TableCell>
              <TableCell
                sx={{ fontWeight: 600, minWidth: 120, textAlign: "center" }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              // Skeleton loaders for loading state
              Array.from({ length: rowsPerPage }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  <TableCell>
                    <Skeleton variant="text" width={40} height={32} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={200} height={32} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={300} height={32} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={100} height={32} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={100} height={32} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={120} height={32} />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 0.5,
                      }}
                    >
                      <Skeleton variant="circular" width={32} height={32} />
                      <Skeleton variant="circular" width={32} height={32} />
                      <Skeleton variant="circular" width={32} height={32} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : productItems?.list?.length === 0 ? (
              // Empty state when no data
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      No product items found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      There are currently no product items available. Add your
                      first product item to get started.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              // Actual data when available
              productItems?.list?.map((row, index) => (
                <ProductItemRow key={row._id} row={row} index={index} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={productItems?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ borderTop: "1px solid rgba(0, 0, 0, 0.12)" }}
      />
    </Paper>
  );
};

export default ProductItemList;
